/* TM Pet Store — theme.js (vanilla, no dependencies) */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const mqNav = window.matchMedia('(min-width: 1200px)');

  /* Toast */
  let toastTimer;
  theme.toast = (message, isError = false) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    el.classList.toggle('toast--error', isError);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => (el.hidden = true), 300);
    }, 2800);
  };

  /* Drawers (menu, cart, filters) with focus trap + scroll lock */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  const Drawer = {
    active: null,
    trigger: null,
    open(id, trigger) {
      const el = document.getElementById(id);
      if (!el) return;
      if (this.active && this.active !== el) this.close(this.active, false);
      this.trigger = trigger || document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      $$(`[aria-controls="${id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      this.active = el;
      const panel = $('.drawer__panel', el);
      setTimeout(() => panel && panel.focus({ preventScroll: true }), 50);
    },
    close(el = this.active, restoreFocus = true) {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      $$(`[aria-controls="${el.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      if (this.active === el) this.active = null;
      if (!this.active) document.body.classList.remove('is-locked');
      if (restoreFocus && this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    }
  };
  theme.drawer = Drawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawer.open(opener.dataset.drawerOpen, opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('[data-drawer]');
      if (drawer && drawer.classList.contains('is-open')) Drawer.close(drawer);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (Drawer.active) Drawer.close();
      closeNavPanels();
    }
    if (e.key === 'Tab' && Drawer.active) {
      const panel = $('.drawer__panel', Drawer.active);
      const items = $$(FOCUSABLE, panel).filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Close the mobile menu drawer automatically when resizing to desktop.
  mqNav.addEventListener('change', (e) => {
    if (e.matches && Drawer.active && Drawer.active.id === 'MenuDrawer') Drawer.close(Drawer.active, false);
  });
  mqDesktop.addEventListener('change', (e) => {
    if (!e.matches) return;
    if (Drawer.active && Drawer.active.hasAttribute('data-facets-drawer')) Drawer.close(Drawer.active, false);
    $$('[data-facets-drawer]').forEach((el) => el.removeAttribute('aria-hidden'));
  });

  /* Header: scroll shadow + accessible dropdowns */
  const header = $('[data-header]');
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function closeNavPanels(except) {
    $$('[data-nav-item].is-open').forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      const toggle = $('[data-nav-toggle]', item);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-nav-toggle]');
    if (toggle) {
      const item = toggle.closest('[data-nav-item]');
      const open = !item.classList.contains('is-open');
      closeNavPanels(item);
      item.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (!e.target.closest('[data-nav-item]')) closeNavPanels();
  });

  /* Announcement bar rotation (mobile/tablet) */
  $$('[data-announcement]').forEach((bar) => {
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    let index = 0;
    let paused = false;
    const interval = parseInt(bar.dataset.interval, 10) || 4000;
    bar.addEventListener('mouseenter', () => (paused = true));
    bar.addEventListener('mouseleave', () => (paused = false));
    bar.addEventListener('focusin', () => (paused = true));
    bar.addEventListener('focusout', () => (paused = false));
    setInterval(() => {
      if (paused || document.hidden || (mqDesktop.matches && !bar.classList.contains('announcement--rotate'))) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, interval);
  });

  /* Search modal (predictive search script is loaded on first open) */
  const searchModal = $('[data-search-modal]');
  if (searchModal) {
    const input = $('[data-search-input]', searchModal);
    let searchLoaded = false;
    const loadSearch = () => {
      if (searchLoaded || !theme.assets || !theme.assets.search) return;
      searchLoaded = true;
      const script = document.createElement('script');
      script.src = theme.assets.search;
      script.defer = true;
      document.head.appendChild(script);
    };

    const openSearch = () => {
      loadSearch();
      if (Drawer.active) Drawer.close(Drawer.active, false);
      if (typeof searchModal.showModal === 'function') searchModal.showModal();
      else searchModal.setAttribute('open', '');
      document.body.classList.add('is-locked');
      setTimeout(() => input && input.focus(), 30);
    };
    const closeSearch = () => {
      if (typeof searchModal.close === 'function') searchModal.close();
      else searchModal.removeAttribute('open');
    };
    searchModal.addEventListener('close', () => {
      if (!Drawer.active) document.body.classList.remove('is-locked');
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-open]')) {
        e.preventDefault();
        openSearch();
      } else if (e.target.closest('[data-search-close]')) {
        closeSearch();
      }
    });
    // Warm up the script when the user shows intent.
    $$('[data-search-open]').forEach((btn) => {
      btn.addEventListener('pointerenter', loadSearch, { once: true, passive: true });
      btn.addEventListener('focus', loadSearch, { once: true });
    });
    // Click on the backdrop closes the dialog.
    searchModal.addEventListener('click', (e) => {
      if (e.target === searchModal) closeSearch();
    });
  }

  /* Product card variant pills */
  document.addEventListener('click', (e) => {
    const pill = e.target.closest('.pills--card .pill[data-variant-id]');
    if (!pill) return;
    const card = pill.closest('[data-product-card]');
    $$('.pill', pill.parentElement).forEach((p) => {
      p.classList.toggle('is-active', p === pill);
      p.setAttribute('aria-checked', String(p === pill));
    });
    const input = $('[data-variant-input]', card);
    if (input) input.value = pill.dataset.variantId;
    const priceWrap = $('[data-card-price]', card);
    if (priceWrap && pill.dataset.priceHtml) priceWrap.innerHTML = pill.dataset.priceHtml;
  });

  /* Quantity steppers */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = $('[data-qty-input]', btn.closest('.qty'));
    if (!input) return;
    const min = parseInt(input.min, 10) || 0;
    const next = Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qtyStep, 10));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart page: submit quantity updates automatically.
  const submitCartPage = debounce((form) => form.submit(), 600);
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-autosubmit-cart]')) submitCartPage(e.target.form);
    if (e.target.matches('select[data-autosubmit]')) e.target.form.submit();
  });

})();
